<template>
  <InfuseModal
    :title-weight="titleWeight"
    :title="actionTitle ?? t('shared.component.infuse.confirmModal.title')"
    @close="emit('close')"
  >
    <form id="confirm" @submit.prevent="onConfirm()">
      <div class="flex max-h-[475px] max-w-[530px] flex-col gap-4 overflow-y-auto">
        <InfuseNote v-if="actionDescription" class="lg:w-[515px]">{{ actionDescription }}</InfuseNote>
        <slot v-else />

        <InfuseField
          v-if="effectiveVerificationRequired"
          :label="t('shared.component.infuse.confirmModal.verify.label', { verifyText: verificationText })"
          size="small"
        >
          <InfuseInput
            v-model="verifyText"
            size="small"
            :placeholder="
              t('shared.component.infuse.confirmModal.verify.placeholder', { verifyText: verificationText })
            "
            autofocus
            required
          />
        </InfuseField>
      </div>
    </form>

    <template #actions>
      <slot
        :is-verify-text-valid="isVerifyTextValid"
        :verification-required="effectiveVerificationRequired"
        name="customActions"
      >
        <InfuseButton data-testid="modal-cancel-button" :disabled="loading" color="secondary" @click="emit('close')">
          {{ t('action.cancel') }}
        </InfuseButton>
        <InfuseButton
          data-testid="modal-confirm-button"
          :disabled="(effectiveVerificationRequired && !isVerifyTextValid) || loading"
          :loading="loading"
          form="confirm"
          type="submit"
        >
          {{ submitText ?? t('action.confirm') }}
        </InfuseButton>
      </slot>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseField, InfuseInput, InfuseModal, InfuseNote } from '@infuse/ui/components';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  actionTitle?: string;
  actionDescription?: string;
  verificationRequired?: boolean;
  verificationText?:
    | 'delete'
    | 'disable'
    | 'infuse'
    | 'remove'
    | 'update'
    | 'accept'
    | 'activate'
    | 'deactivate'
    | 'move';
  submitText?: string;
  loading?: boolean;
  titleWeight?: 'bold' | 'normal';
}

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  actionTitle: undefined,
  actionDescription: undefined,
  verificationRequired: true,
  verificationText: 'infuse',
  submitText: undefined,
  loading: false,
  titleWeight: 'bold'
});

const effectiveVerificationRequired = computed(
  () => import.meta.env.VITE_UI_DISABLE_CONFIRM_VERIFICATION !== 'true' && props.verificationRequired
);

interface Emits {
  (e: 'close'): void;
  (e: 'confirm'): void;
}

const emit = defineEmits<Emits>();

const verifyText = ref<string>();
const isVerifyTextValid = computed(() => verifyText.value === props.verificationText);

function onConfirm() {
  emit('confirm');
}
</script>
